import styled from '@emotion/styled';
import { useState } from 'react';
import {
  Button,
  Field,
  SessionActions,
  StatusPill,
  ToggleGroup,
  ChevronRightIcon,
  CheckIcon,
  CreditCardIcon,
  RulerIcon,
  colors,
  fonts,
  breakpoints,
} from '../../../ui';
import {
  Center,
  Muted,
  Headline,
  Accent,
  FieldRow,
  StatusCards,
  TileRow,
  MethodTile,
  StatusDots,
  StatusLine,
  CAMERA_STATUS_LABEL,
  AccordionRoot,
  AccordionHeader,
  AccordionBody,
  filledField,
} from './shared';
import { CreditCard } from './CreditCard';

// Presentational clinician calibration screens (Figma C2-C5.1). No context access here: the
// containers in screens/optician/Calibration/* and screens/optician/set-distance.js own the ws
// messages, calibration maths and session-state sync, and pass plain props. The Showcase renders
// these directly (?view=cal-...).

const phone = `@media (max-width: ${breakpoints.phone - 1}px)`;
const mm = (v) => (v || v === 0 ? `${Math.round(v)}mm` : '--');

// ---------------------------------------------------------------------------------------------
// C2 / C4  Calibration Selection
// ---------------------------------------------------------------------------------------------
export const CalibrationSelectionView = ({ screenCalibrated, cameraCalibrated, onCalibrateScreen, onCalibrateCamera, onNext, responsiveNote }) => (
  <Center gap={24}>
    {responsiveNote && (
      <StatusPill tone='warning' role='note'>
        Patient appears to be using a phone or tablet.
      </StatusPill>
    )}
    <StatusCards
      screen={screenCalibrated}
      camera={cameraCalibrated}
      screenAction={
        <Button size='xl' onClick={onCalibrateScreen} data-testid='calibrate-screen'>
          {screenCalibrated ? 'Recalibrate' : 'Calibrate Now'}
        </Button>
      }
      cameraAction={
        <Button size='xl' onClick={onCalibrateCamera} data-testid='calibrate-camera'>
          {cameraCalibrated ? 'Recalibrate' : 'Calibrate Now'}
        </Button>
      }
    />
    <SessionActions primary={{ label: 'Next', onClick: onNext, disabled: !screenCalibrated }} />
  </Center>
);

// ---------------------------------------------------------------------------------------------
// C3  Screen calibration method (intro left, accordion right)
// ---------------------------------------------------------------------------------------------
const MethodGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 0.8fr) minmax(0, 1.2fr);
  gap: 32px;
  width: 100%;
  margin: auto 0;
  align-items: start;
  font-family: ${fonts.family};
  .intro h2 {
    margin: 0 0 20px;
    font-size: 18px;
    line-height: 1.45;
    font-weight: 700;
    color: ${colors.heading};
  }
  ${phone} {
    grid-template-columns: minmax(0, 1fr);
    gap: 16px;
    .intro h2 {
      margin-bottom: 8px;
      font-size: 16px;
    }
    .intro .extra {
      display: none;
    }
  }
`;

export const SCREEN_METHODS = [
  {
    id: 'device',
    title: 'Use Device Measurement',
    button: 'Use Device Measurements',
    steps: [
      'This method only works for Mobile Devices and Tablets.',
      "On the calibration tool's initial screen, ask the patient to tap the 'Select Make' and 'Select Device' dropdowns.",
      'The patient should see a dropdown list of supported devices. Ask the patient to choose their device model from the list.',
      'Once they have selected their device, the calibration tool will automatically retrieve the screen dimensions associated with that model.',
    ],
  },
  {
    id: 'card',
    title: 'Use Card',
    button: 'Use Card',
    steps: [
      'This method works on tablets, laptops and desktop screens (not phones).',
      'Ask the patient to put their device on a flat surface and hold a standard bank or ID card against the screen.',
      'The patient resizes the on-screen card with the − and + buttons until it matches their card, then taps Submit.',
      'The card you see here grows and shrinks with the patient’s changes. Click Next when they are done.',
    ],
  },
  {
    id: 'ruler',
    title: 'Use Ruler',
    button: 'Use Ruler',
    steps: [
      'Works on any device. The patient needs a ruler or a tape measure.',
      'Ask the patient to measure the width and height of their screen in millimetres, excluding the bezel.',
      'The patient types both values and taps Submit. The values appear here.',
    ],
  },
];

export const ScreenMethodView = ({ onSelect, onBack, initial = 'device', cardDisabledReason }) => {
  const [open, setOpen] = useState(initial);
  return (
    <MethodGrid>
      <div className='intro'>
        <h2>Select one of the following calibration methods:</h2>
        <Muted align='left' small>
          Calibrating the device accurately is a fundamental step in remote vision testing, and we offer multiple methods to ensure precision.
        </Muted>
        <Muted className='extra' align='left' small style={{ marginTop: 12 }}>
          By providing this alternative method, we aim to accommodate varying preferences and equipment availability, all while maintaining the highest standards of testing precision.
        </Muted>
        <Muted align='left' small style={{ marginTop: 12 }}>
          Before you start, ask the patient to turn their screen brightness up fully and to enter full screen.
        </Muted>
      </div>
      <AccordionRoot data-testid='screen-method-accordion'>
        {SCREEN_METHODS.map((m) => {
          const isOpen = open === m.id;
          const disabled = m.id === 'card' && cardDisabledReason;
          return (
            <div key={m.id}>
              <AccordionHeader type='button' isOpen={isOpen} aria-expanded={isOpen} onClick={() => setOpen(isOpen ? null : m.id)}>
                <ChevronRightIcon size={16} />
                {m.title}
              </AccordionHeader>
              {isOpen && (
                <AccordionBody boldFirst={m.id === 'device'}>
                  <ol>
                    {m.steps.map((s) => (
                      <li key={s}>{s}</li>
                    ))}
                  </ol>
                  {disabled && (
                    <Muted align='left' small style={{ color: colors.error[600], marginBottom: 12 }}>
                      {cardDisabledReason}
                    </Muted>
                  )}
                  <Button size='md' onClick={() => onSelect(m.id)} disabled={Boolean(disabled)} data-testid={`method-${m.id}`}>
                    {m.button}
                  </Button>
                </AccordionBody>
              )}
            </div>
          );
        })}
      </AccordionRoot>
      <SessionActions onBack={onBack} primary={{ label: 'Next', disabled: true }} />
    </MethodGrid>
  );
};

// ---------------------------------------------------------------------------------------------
// C3.1 / C3.2  Waiting for the patient's device / device selected
// ---------------------------------------------------------------------------------------------
export const FallbackTiles = ({ onUseCard, onUseRuler, cardDisabled }) => (
  <TileRow>
    <MethodTile type='button' filled onClick={onUseCard} disabled={cardDisabled} data-testid='fallback-card'>
      <CreditCardIcon size={48} strokeWidth={1.4} />
      Use Card
    </MethodTile>
    <MethodTile type='button' onClick={onUseRuler} data-testid='fallback-ruler'>
      <RulerIcon size={48} strokeWidth={1.4} />
      Use Ruler
    </MethodTile>
  </TileRow>
);

export const DeviceWaitView = ({ patientName = 'The patient', device, heightMm, widthMm, onUseCard, onUseRuler, onBack, onNext, cardDisabled }) => {
  const selected = Boolean(device && (heightMm || widthMm));
  return (
    <Center gap={20}>
      {selected ? (
        <Headline data-testid='device-selected'>
          {patientName} has selected the
          <Accent>{device}</Accent>
        </Headline>
      ) : (
        <>
          <Headline>Please wait for the patient to select their device</Headline>
          <span style={{ fontFamily: fonts.family, fontSize: 18, color: colors.slate[400] }}>Device Name</span>
        </>
      )}
      <FieldRow>
        <Field label='Patient Device Height' accent={selected} readOnly style={selected ? filledField : undefined} value={selected ? mm(heightMm) : '--'} />
        <Field label='Patient Device Width' accent={selected} readOnly style={selected ? filledField : undefined} value={selected ? mm(widthMm) : '--'} />
      </FieldRow>
      <Muted>If the patient can’t find their device, they can use one of the methods listed below to measure their screen size.</Muted>
      <FallbackTiles onUseCard={onUseCard} onUseRuler={onUseRuler} cardDisabled={cardDisabled} />
      <SessionActions onBack={onBack} primary={{ label: 'Next', onClick: onNext, disabled: !selected }} />
    </Center>
  );
};

// ---------------------------------------------------------------------------------------------
// Card (clinician mirror of the patient's card) and Ruler readout
// ---------------------------------------------------------------------------------------------
const CardStage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 180px;
  overflow: auto;
  padding: 8px;
  box-sizing: border-box;
`;

export const CardMirrorView = ({ widthPx, heightPx, received, onBack, onNext }) => (
  <Center gap={20}>
    <Headline>Card Measurement</Headline>
    <Muted>The card below grows or shrinks as the patient adjusts it. Click “Next” when the patient has matched the card and tapped Submit.</Muted>
    <CardStage>
      <CreditCard width={widthPx || 355} height={heightPx || 222} submitted={received} />
    </CardStage>
    {received ? (
      <StatusPill tone='success'>
        <CheckIcon size={14} strokeWidth={3} /> Patient has submitted the card size
      </StatusPill>
    ) : (
      <StatusPill tone='neutral'>Waiting for the patient…</StatusPill>
    )}
    <SessionActions onBack={onBack} primary={{ label: 'Next', onClick: onNext }} />
  </Center>
);

export const RulerReadoutView = ({ widthMm, heightMm, received, onBack, onNext }) => (
  <Center gap={20}>
    <Headline>{received ? 'The patient has sent their screen size' : 'Please wait for the patient to measure their screen'}</Headline>
    <Muted>Ask the patient to measure the width and height of their screen in millimetres, excluding the bezel, and to tap Submit.</Muted>
    <FieldRow>
      <Field label='Patient Screen Width' accent={received} readOnly style={received ? filledField : undefined} value={received ? mm(widthMm) : '--'} />
      <Field label='Patient Screen Height' accent={received} readOnly style={received ? filledField : undefined} value={received ? mm(heightMm) : '--'} />
    </FieldRow>
    {received && (
      <StatusPill tone='success'>
        <CheckIcon size={14} strokeWidth={3} /> Values received
      </StatusPill>
    )}
    <SessionActions onBack={onBack} primary={{ label: 'Next', onClick: onNext, disabled: !received }} />
  </Center>
);

// ---------------------------------------------------------------------------------------------
// Camera: method (IBIS-Tool / Blindspot) and C4.1 calibration
// ---------------------------------------------------------------------------------------------
export const CameraMethodView = ({ onSelect, onBack }) => (
  <Center gap={20}>
    <Headline>Select how you want to calibrate the patient’s camera.</Headline>
    <Muted>The IBIS-Tool method needs the printed IBIS calibration tool. The blindspot method needs the patient sitting 50cm from the screen.</Muted>
    <TileRow>
      <MethodTile type='button' filled onClick={() => onSelect('camera-calibration')} data-testid='camera-method-tool'>
        IBIS-Tool
      </MethodTile>
      <MethodTile type='button' onClick={() => onSelect('blindspot-camera-calibration')} data-testid='camera-method-blindspot'>
        Blindspot
      </MethodTile>
    </TileRow>
    <SessionActions onBack={onBack} primary={{ label: 'Next', disabled: true }} />
  </Center>
);

export const CameraCalibrationView = ({ status = 0, instruction, onBegin, onBack, onNext }) => (
  <Center gap={28}>
    <Headline style={{ fontWeight: 600, color: colors.slate[600] }}>{instruction || 'Please wait for the patient to position the tool, then click "Begin Calibration".'}</Headline>
    <Button size='lg' onClick={onBegin} disabled={status === 1} data-testid='begin-camera-calibration'>
      {status === 2 ? 'Calibrate Again' : 'Begin Calibration'}
    </Button>
    <StatusLine aria-live='polite'>
      Calibration Status: <b>{CAMERA_STATUS_LABEL[status]}</b>
    </StatusLine>
    <StatusDots status={status} />
    <SessionActions onBack={onBack} backDisabled={status === 1} primary={{ label: 'Next', onClick: onNext, disabled: status !== 2 }} />
  </Center>
);

// ---------------------------------------------------------------------------------------------
// C5 / C5.1  Set Distance
// ---------------------------------------------------------------------------------------------
export const DISTANCE_MODES = [
  { value: 'faceRecognition', label: 'Use Facial Recognition' },
  { value: 'manual', label: 'Input Manually' },
];

const LinkButton = styled(Button)`
  font-size: 13px;
`;

export const SetDistanceView = ({
  method,
  onMethodChange,
  distanceMm,
  detecting,
  positioned,
  onToggleDetection,
  manualValue,
  onManualChange,
  blindspotControl,
  onBack,
  onNext,
  nextDisabled,
}) => (
  <Center gap={24}>
    <Headline style={{ fontWeight: 600, color: colors.slate[600] }}>Select how you want to set the patient distance from the screen.</Headline>
    <ToggleGroup ariaLabel='Distance method' value={method === 'blindspot' ? null : method} onChange={onMethodChange} options={DISTANCE_MODES} />
    {method === 'faceRecognition' && (
      <Center top gap={10}>
        <Field label={detecting ? 'Collecting Distance…' : 'Collected Distance'} readOnly value={distanceMm ? `${Math.round(distanceMm)}mm` : '--mm'} data-testid='collected-distance' />
        <LinkButton variant='tertiary' onClick={onToggleDetection} data-testid='toggle-detection'>
          {detecting ? 'Stop Detection' : 'Start Detection'}
        </LinkButton>
        {positioned && !detecting && (
          <StatusPill tone='success'>
            <CheckIcon size={13} strokeWidth={3} /> Patient Positioned
          </StatusPill>
        )}
      </Center>
    )}
    {method === 'manual' && (
      <Center top gap={10}>
        <Field
          label='Type Distance'
          type='number'
          inputMode='numeric'
          min='100'
          placeholder='--mm'
          value={manualValue ?? ''}
          onChange={(e) => onManualChange(e.target.value)}
          data-testid='manual-distance'
        />
        <Muted small>Distance from the patient’s eyes to the screen, in millimetres.</Muted>
      </Center>
    )}
    {method === 'blindspot' && <div style={{ width: '100%', maxWidth: 620 }}>{blindspotControl}</div>}
    <LinkButton variant='tertiary' onClick={() => onMethodChange(method === 'blindspot' ? 'faceRecognition' : 'blindspot')} data-testid='toggle-blindspot'>
      {method === 'blindspot' ? 'Back to facial recognition' : 'Use the blindspot method instead'}
    </LinkButton>
    <SessionActions onBack={onBack} primary={{ label: 'Next', onClick: onNext, disabled: nextDisabled }} />
  </Center>
);
